import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import SEO from '../components/SEO'
import { BLOG_META } from '../data/meta'

export default function Blog() {
  const [posts, setPosts] = useState([])
  const [categories, setCategories] = useState([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [categoryFilter, setCategoryFilter] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    const params = new URLSearchParams()
    if (categoryFilter) params.set('category', categoryFilter)
    if (search) params.set('q', search)
    params.set('page', String(page))
    params.set('limit', '9')

    setLoading(true)
    Promise.all([
      fetch(`/api/blog/posts?${params}`),
      fetch('/api/blog/categories')
    ])
      .then(async ([postsRes, catRes]) => {
        const postsData = await postsRes.json()
        const catData = await catRes.json()
        setPosts(postsData.posts || [])
        setTotalPages(postsData.pages || 1)
        setCategories(catData.categories || [])
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [page, categoryFilter, search])

  const buildPaginationLinks = () => {
    const links = []
    if (page > 1) {
      links.push(<link key="prev" rel="prev" href={`https://www.tredextechnology.in/blog?page=${page - 1}${categoryFilter ? '&category=' + categoryFilter : ''}${search ? '&q=' + encodeURIComponent(search) : ''}`} />)
    }
    if (page < totalPages) {
      links.push(<link key="next" rel="next" href={`https://www.tredextechnology.in/blog?page=${page + 1}${categoryFilter ? '&category=' + categoryFilter : ''}${search ? '&q=' + encodeURIComponent(search) : ''}`} />)
    }
    return links
  }

  return (
    <>
      <SEO title={BLOG_META.title} description={BLOG_META.description} canonical={BLOG_META.canonical} keywords={BLOG_META.keywords} structuredData={BLOG_META.structuredData}>
        {buildPaginationLinks()}
      </SEO>
      <Navbar />
      <div className="blog-page">
        <div className="container"><br></br>
          <div className="blog-hero"><br></br>
            <span className="section-badge"><i className="fas fa-pen-nib"></i> Blog</span>
            <h1 className="section-title">Insights from the <span className="gradient-text">AI Frontier</span></h1>
             <p className="section-description">Practical insights on adding AI features to your business website such as chatbots, smart search, lead generation, and SEO automation.</p>
          </div>

          <div className="blog-layout">
            <div className="blog-main">
              <div className="blog-search">
                <input type="text" placeholder="Search articles..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} />
                <i className="fas fa-search"></i>
              </div>

              <div className="blog-filters">
                <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(1) }}>
                  <option value="">All Categories</option>
                  {categories.map(cat => <option key={cat.id} value={cat.slug}>{cat.name}</option>)}
                </select>
              </div>

              {loading && <div className="blog-loading">Loading posts...</div>}

              <div className="blog-grid">
                {posts.map(post => (
                  <article key={post.id} className="blog-card">
                    {post.cover_image && (
                      <div className="blog-card-image">
                        <img src={post.cover_image} alt={post.title} loading="lazy" />
                      </div>
                    )}
                    <div className="blog-card-body">
                      <div className="blog-card-meta">
                        {post.category_name && <span className="category-badge">{post.category_name}</span>}
                        <span className="blog-date">{new Date(post.published_at || post.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                      </div>
                      <h2><Link to={`/blog/${post.slug}`}>{post.title}</Link></h2>
                      <p>{post.excerpt}</p>
                      <div className="blog-card-footer">
                        <span className="blog-reading-time"><i className="fas fa-clock"></i> {post.reading_time || 5} min read</span>
                        <Link to={`/blog/${post.slug}`} className="btn btn-outline btn-sm">Read More <i className="fas fa-arrow-right"></i></Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {posts.length === 0 && !loading && (
                <div className="blog-empty">
                  <i className="fas fa-newspaper" style={{ fontSize: '3rem', color: 'var(--text-muted)', marginBottom: '16px' }}></i>
                  <p>No posts found matching your criteria.</p>
                </div>
              )}

              {totalPages > 1 && (
                <div className="blog-pagination">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Previous</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <button key={p} className={page === p ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
                  ))}
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next</button>
                </div>
              )}
            </div>

            <aside className="blog-sidebar">
              <div className="sidebar-widget">
                <h3>Categories</h3>
                <ul>
                  {categories.map(cat => (
                    <li key={cat.id} className={categoryFilter === cat.slug ? 'active' : ''} onClick={() => setCategoryFilter(categoryFilter === cat.slug ? '' : cat.slug)}>
                      {cat.name} <span>({cat.post_count})</span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </div>
      <Footer />
    </>
  )
}
